import { CashFlowStrategy } from "projects/shared-components/adjustment-control-panel/cash-flow-strategy";
import * as shortid from "shortid";

// CR-20260928-001 — every APG portfolio is a REAL portfolio: own id, own name, FIXED underlying and
// strategy. The old "<No Portfolio>" scratchpad (fixed id '----', asset following whatever the
// trader selected) is gone. The one thing that survives from it is a ROLE: any portfolio can be
// flagged `isDefault`, and that portfolio is (1) the boot fallback when no last-used portfolio can
// be restored and (2) the fallback selection after a delete. Nothing else reads the flag.
//
// Invariant per user: at most ONE portfolio carries `isDefault` (the portfolios service clears the
// others on save). Readers tolerate zero or many by taking the first.

export const DefaultPortfolioName = 'Default Portfolio';

export interface ApgPortfolio {
   id: string;
   name: string;
   asset?: string;
   strategy?: CashFlowStrategy;
   userId?: string;
   userName?: string;
   // HG independent-portfolio fields (CR-20260606-001). APG ignores these.
   // isIndependent: a Hedging-Grid-only book with no APG parent.
   // defaultQty: per-portfolio base order qty (APG-bound portfolios derive this from their template instead).
   isIndependent?: boolean;
   defaultQty?: number;
   // CR-20260928-001: the boot/fallback role. At most one per user.
   isDefault?: boolean;
}

/**
 * The portfolio the panel falls back to: the flagged one, else the first by name, else undefined.
 * Every caller MUST handle undefined — a user with no portfolios is a legal state.
 */
export function findDefaultPortfolio(portfolios: ApgPortfolio[]): ApgPortfolio | undefined {
   const list = portfolios || [];
   const flagged = list.find(x => !!x && x.isDefault);
   if (flagged) {
      return flagged;
   }
   const sorted = list.filter(x => !!x).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
   return sorted[0];
}

/** Default first, then by name. The order every portfolio list shows. */
export function sortPortfolios(portfolios: ApgPortfolio[]): ApgPortfolio[] {
   return (portfolios || []).slice().sort((a, b) => {
      const da = a.isDefault ? 0 : 1;
      const db = b.isDefault ? 0 : 1;
      if (da !== db) {
         return da - db;
      }
      return (a.name || '').localeCompare(b.name || '');
   });
}

/**
 * The seed for a user who has no portfolio yet (ruling Q8: created lazily, in the browser, on the
 * first read of an EMPTY own list). A real portfolio like any other, flagged default.
 */
export function makeSeedDefaultPortfolio(userId: string, userName: string, asset: string, strategy: CashFlowStrategy): ApgPortfolio {
   return {
      id: shortid.generate(),
      name: DefaultPortfolioName,
      userId,
      userName,
      asset,
      strategy,
      isDefault: true
   };
}
